import { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { Save, AlertTriangle, Hash, Lock, Mail, Shield } from 'lucide-react';
import type { EncryptionMode } from '../../types';

const cardStyle: React.CSSProperties = {
    background: 'var(--bg-secondary)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-color)',
    overflow: 'hidden'
};

const headerStyle: React.CSSProperties = {
    padding: '16px 20px',
    borderBottom: '1px solid var(--border-color)'
};

export function GlobalSettings() {
    const { settings, updateSettings, channels, updateChannel, hasPermission } = useApp();

    const [timer, setTimer] = useState(300);
    const [allowCustom, setAllowCustom] = useState(true);
    const [allowMultipleAssignees, setAllowMultipleAssignees] = useState(false);
    const [enableReadReceipts, setEnableReadReceipts] = useState(true);
    const [requireInviteAcceptance, setRequireInviteAcceptance] = useState(false);
    const [maxFileSize, setMaxFileSize] = useState(10);
    const [allowedFileTypes, setAllowedFileTypes] = useState<string[]>(['image', 'document']);

    // Storage
    const [storageProvider, setStorageProvider] = useState<'local' | 's3'>('local');
    const [s3Bucket, setS3Bucket] = useState('');
    const [s3Region, setS3Region] = useState('');
    const [s3Endpoint, setS3Endpoint] = useState('');
    const [s3AccessKey, setS3AccessKey] = useState('');
    const [s3SecretKey, setS3SecretKey] = useState('');
    const [s3IgnoreSsl, setS3IgnoreSsl] = useState(false);

    // SMTP Email Settings
    const [emailProvider, setEmailProvider] = useState<'smtp' | 'resend' | 'gmail'>('smtp');
    const [resendApiKey, setResendApiKey] = useState('');
    const [smtpHost, setSmtpHost] = useState('');
    const [smtpPort, setSmtpPort] = useState(587);
    const [smtpUser, setSmtpUser] = useState('');
    const [smtpPassword, setSmtpPassword] = useState('');
    const [smtpSecure, setSmtpSecure] = useState(false);
    const [smtpFromEmail, setSmtpFromEmail] = useState('');
    const [smtpFromName, setSmtpFromName] = useState('Tock');

    // 2FA Settings
    const [require2FA, setRequire2FA] = useState(false);
    const [requireOauthRecovery, setRequireOauthRecovery] = useState(false);
    const [whitelistedIPs, setWhitelistedIPs] = useState('');

    // User Settings
    const [allowProfileEdit, setAllowProfileEdit] = useState(true);
    const [selfDestructBehavior, setSelfDestructBehavior] = useState<'delete' | 'vanish'>('delete');
    const [maxRetentionHours, setMaxRetentionHours] = useState<number>(24);

    // File Retention Policy
    const [fileRetentionEnabled, setFileRetentionEnabled] = useState(false);
    const [fileSizeThreshold, setFileSizeThreshold] = useState(5);
    const [largeFileRetentionDays, setLargeFileRetentionDays] = useState(90);
    const [smallFileRetentionDays, setSmallFileRetentionDays] = useState(30);
    const [showRetentionWarning, setShowRetentionWarning] = useState(true);
    const [allowUserNeverDelete, setAllowUserNeverDelete] = useState(true);
    const [maxUploadSize, setMaxUploadSize] = useState(50);
    const [smallFileCleanupInterval, setSmallFileCleanupInterval] = useState(24);
    const [largeFileCleanupInterval, setLargeFileCleanupInterval] = useState(24);
    const [smallFileGracePeriod, setSmallFileGracePeriod] = useState(48);
    const [largeFileGracePeriod, setLargeFileGracePeriod] = useState(48);
    const [smallFileCleanupActive, setSmallFileCleanupActive] = useState(true);
    const [largeFileCleanupActive, setLargeFileCleanupActive] = useState(true);
    const [retentionNotifyBefore, setRetentionNotifyBefore] = useState(3);
    const [enableGracePeriod, setEnableGracePeriod] = useState(true);
    const [enableExpiryNotifications, setEnableExpiryNotifications] = useState(false);
    const [enableFileDeduplication, setEnableFileDeduplication] = useState(true);
    const [enableImageCompression, setEnableImageCompression] = useState(false);
    const [imageCompressionQuality, setImageCompressionQuality] = useState(85);
    const [allowUserDisableCompression, setAllowUserDisableCompression] = useState(true);

    const [runningCleanup, setRunningCleanup] = useState(false);

    // Encryption Policy
    const [channelEncryptionMode, setChannelEncryptionMode] = useState<EncryptionMode>('optional');
    const [dmEncryptionMode, setDmEncryptionMode] = useState<EncryptionMode>('optional');
    const [topicEncryptionMode, setTopicEncryptionMode] = useState<EncryptionMode>('optional');

    // Desktop App Settings
    const [desktopSyncWindowDays, setDesktopSyncWindowDays] = useState(30);
    const [desktopAttachmentSyncDays, setDesktopAttachmentSyncDays] = useState(7);
    const [desktopMaxMessages, setDesktopMaxMessages] = useState(5000);
    const [desktopAllowAttachments, setDesktopAllowAttachments] = useState(true);
    const [disableEmojis, setDisableEmojis] = useState(false);

    // E2EE Escrow
    const [escrowEmailRecipient, setEscrowEmailRecipient] = useState('');

    // OAuth Recovery
    const [googleClientId, setGoogleClientId] = useState('');
    const [microsoftClientId, setMicrosoftClientId] = useState('');
    const [microsoftTenantId, setMicrosoftTenantId] = useState('common');
    const [oauthRecoveryEmail, setOauthRecoveryEmail] = useState('');

    // Sensitive Data Warnings
    const [warnOnCreditCard, setWarnOnCreditCard] = useState(true);
    const [warnOnSsn, setWarnOnSsn] = useState(true);
    const [warnOnPassword, setWarnOnPassword] = useState(true);

    const [appUrl, setAppUrl] = useState('');

    // Unread message email notifications
    const [enableUnreadEmailNotifications, setEnableUnreadEmailNotifications] = useState(false);

    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<string | null>(null);

    // Define all possible sub-tabs with their permission keys
    const ALL_SETTINGS_TABS = [
        { key: 'general', label: 'General', permission: 'settings:general' },
        { key: 'email', label: 'Email', permission: 'settings:email' },
        { key: 'storage', label: 'Storage', permission: 'settings:storage' },
        { key: 'security', label: 'Security', permission: 'settings:security' },
        { key: 'data', label: 'Data Policy', permission: 'settings:data' },
        { key: 'desktop', label: 'Desktop App', permission: 'settings:desktop' },
    ] as const;

    const isSuperAdmin = hasPermission('super:admin') || hasPermission('admin:full');
    const visibleSettingsTabs = ALL_SETTINGS_TABS.filter(t => isSuperAdmin || hasPermission(t.permission));

    const [activeTab, setActiveTab] = useState<typeof ALL_SETTINGS_TABS[number]['key']>(
        visibleSettingsTabs[0]?.key ?? 'general'
    );



    useEffect(() => {
        if (settings) {
            setTimer(settings.default_self_destruct_timer);
            setAllowCustom(settings.allow_user_custom_timer);
            setAllowMultipleAssignees(settings.allow_multiple_assignees || false);
            setEnableReadReceipts(settings.enable_read_receipts !== undefined ? settings.enable_read_receipts : true);
            setRequireInviteAcceptance(settings.require_invite_acceptance || false);
            setAllowProfileEdit(settings.allow_profile_edit !== undefined ? settings.allow_profile_edit : true);
            setSelfDestructBehavior(settings.self_destruct_behavior || 'delete');
            setMaxRetentionHours((settings.max_self_destruct_retention || 86400) / 3600);
            setMaxFileSize(settings.max_file_size_mb || 10);
            setAllowedFileTypes(settings.allowed_file_types || ['image', 'document']);
            setStorageProvider(settings.storage_provider || 'local');
            setS3Bucket(settings.s3_bucket || '');
            setS3Region(settings.s3_region || '');
            setS3Endpoint(settings.s3_endpoint || '');
            setS3AccessKey(settings.s3_access_key || '');
            setS3SecretKey(settings.s3_secret_key || '');
            setS3IgnoreSsl(settings.s3_ignore_ssl || false);
            // SMTP
            setEmailProvider(settings.email_provider || 'smtp');
            setResendApiKey(settings.resend_api_key || '');
            setSmtpHost(settings.smtp_host || '');
            setSmtpPort(settings.smtp_port || 587);
            setSmtpUser(settings.smtp_user || '');
            setSmtpPassword(settings.smtp_password || '');
            setSmtpSecure(settings.smtp_secure || false);
            setSmtpFromEmail(settings.smtp_from_email || '');
            setSmtpFromName(settings.smtp_from_name || 'TicketChat');
            // 2FA
            setRequire2FA(settings.require_2fa || false);
            setRequireOauthRecovery(settings.require_oauth_recovery || false);
            setWhitelistedIPs((settings.whitelisted_ips || []).join(', '));
            // File Retention
            setFileRetentionEnabled(!!settings.file_retention_enabled);
            setFileSizeThreshold(settings.file_size_threshold_mb || 5);
            setLargeFileRetentionDays(settings.large_file_retention_days || 90);
            setSmallFileRetentionDays(settings.small_file_retention_days || 30);
            setShowRetentionWarning(settings.show_retention_warning !== undefined ? settings.show_retention_warning : true);
            setAllowUserNeverDelete(settings.allow_user_never_delete !== undefined ? settings.allow_user_never_delete : true);
            setMaxUploadSize(settings.max_upload_size_mb || 50);
            setSmallFileCleanupInterval(settings.small_file_cleanup_interval_hours || 24);
            setLargeFileCleanupInterval(settings.large_file_cleanup_interval_hours || 24);
            setSmallFileGracePeriod(settings.small_file_grace_period_hours || 48);
            setLargeFileGracePeriod(settings.large_file_grace_period_hours || 48);
            setRetentionNotifyBefore(settings.retention_notify_before_days || 3);
            setEnableGracePeriod(settings.enable_grace_period !== undefined ? settings.enable_grace_period : true);
            setEnableExpiryNotifications(settings.enable_expiry_notifications || false);
            setEnableFileDeduplication(settings.enable_file_deduplication !== undefined ? settings.enable_file_deduplication : true);
            setEnableImageCompression(settings.enable_image_compression || false);
            setImageCompressionQuality(settings.image_compression_quality || 85);
            setAllowUserDisableCompression(settings.allow_user_disable_compression !== undefined ? settings.allow_user_disable_compression : true);
            setSmallFileCleanupActive(settings.small_file_cleanup_active !== undefined ? settings.small_file_cleanup_active : true);
            setLargeFileCleanupActive(settings.large_file_cleanup_active !== undefined ? settings.large_file_cleanup_active : true);

            // Encryption Policy
            if (settings.encryption_policy) {
                setChannelEncryptionMode(settings.encryption_policy.channel_mode || 'optional');
                setDmEncryptionMode(settings.encryption_policy.dm_mode || 'optional');
                setTopicEncryptionMode(settings.encryption_policy.topic_mode || 'optional');
            }

            // Desktop App
            setDesktopSyncWindowDays(settings.desktop_sync_window_days || 30);
            setDesktopAttachmentSyncDays(settings.desktop_attachment_sync_days || 7);
            setDesktopMaxMessages(settings.desktop_max_messages_channel || 5000);
            setDesktopAllowAttachments(settings.desktop_allow_attachments !== undefined ? settings.desktop_allow_attachments : true);
            setDisableEmojis(!!settings.disable_emojis);

            // E2EE Escrow
            setEscrowEmailRecipient((settings as any).escrow_email_recipient || '');
            setOauthRecoveryEmail((settings as any).oauth_recovery_email || '');
            setWarnOnCreditCard(settings.warn_on_credit_card ?? true);
            setWarnOnSsn(settings.warn_on_ssn ?? true);
            setWarnOnPassword(settings.warn_on_password ?? true);

            // OAuth Recovery
            setGoogleClientId((settings as any).google_client_id || '');
            setMicrosoftClientId((settings as any).microsoft_client_id || '');
            setMicrosoftTenantId((settings as any).microsoft_tenant_id || 'common');

            // Application URL
            setAppUrl(settings.app_url || '');

            // Unread email notifications
            setEnableUnreadEmailNotifications(!!(settings.enable_unread_email_notifications));
        }
    }, [settings]);

    const handleSave = async () => {
        setSaving(true);
        setMessage(null);
        console.log('[GlobalSettings] Saving settings. fileRetentionEnabled state:', fileRetentionEnabled);
        try {
            await updateSettings({
                default_self_destruct_timer: timer,
                allow_user_custom_timer: allowCustom,
                allow_multiple_assignees: allowMultipleAssignees,
                enable_read_receipts: enableReadReceipts,
                require_invite_acceptance: requireInviteAcceptance,
                allow_profile_edit: allowProfileEdit,
                self_destruct_behavior: selfDestructBehavior,
                max_self_destruct_retention: maxRetentionHours * 3600,
                max_file_size_mb: maxFileSize,
                allowed_file_types: allowedFileTypes,
                storage_provider: storageProvider,
                s3_bucket: s3Bucket,
                s3_region: s3Region,
                s3_endpoint: s3Endpoint,
                s3_access_key: s3AccessKey,
                s3_secret_key: s3SecretKey,
                s3_ignore_ssl: s3IgnoreSsl,
                // SMTP
                email_provider: emailProvider,
                resend_api_key: resendApiKey,
                smtp_host: smtpHost,
                smtp_port: smtpPort,
                smtp_user: smtpUser,
                smtp_password: smtpPassword,
                smtp_secure: smtpSecure,
                smtp_from_email: smtpFromEmail,
                smtp_from_name: smtpFromName,
                small_file_cleanup_active: smallFileCleanupActive,
                large_file_cleanup_active: largeFileCleanupActive,
                // 2FA
                require_2fa: require2FA,
                require_oauth_recovery: requireOauthRecovery,
                whitelisted_ips: whitelistedIPs.split(',').map(ip => ip.trim()).filter(Boolean),
                // File Retention
                file_retention_enabled: !!fileRetentionEnabled,
                file_size_threshold_mb: fileSizeThreshold,
                large_file_retention_days: largeFileRetentionDays,
                small_file_retention_days: smallFileRetentionDays,
                show_retention_warning: showRetentionWarning,
                allow_user_never_delete: allowUserNeverDelete,
                max_upload_size_mb: maxUploadSize,
                small_file_cleanup_interval_hours: smallFileCleanupInterval,
                large_file_cleanup_interval_hours: largeFileCleanupInterval,
                small_file_grace_period_hours: smallFileGracePeriod,
                large_file_grace_period_hours: largeFileGracePeriod,
                retention_notify_before_days: retentionNotifyBefore,
                enable_grace_period: enableGracePeriod,
                enable_expiry_notifications: enableExpiryNotifications,
                enable_file_deduplication: enableFileDeduplication,
                enable_image_compression: enableImageCompression,
                image_compression_quality: imageCompressionQuality,
                allow_user_disable_compression: allowUserDisableCompression,

                // Encryption Policy
                encryption_policy: {
                    channel_mode: channelEncryptionMode,
                    dm_mode: dmEncryptionMode,
                    topic_mode: topicEncryptionMode
                },

                // Desktop App
                desktop_sync_window_days: desktopSyncWindowDays,
                desktop_attachment_sync_days: desktopAttachmentSyncDays,
                desktop_max_messages_channel: desktopMaxMessages,
                desktop_allow_attachments: desktopAllowAttachments,
                disable_emojis: disableEmojis,

                // E2EE Escrow
                escrow_email_recipient: escrowEmailRecipient,

                // OAuth Recovery
                google_client_id: googleClientId,
                microsoft_client_id: microsoftClientId,
                microsoft_tenant_id: microsoftTenantId,
                oauth_recovery_email: oauthRecoveryEmail,

                // Sensitive Data Warnings
                warn_on_credit_card: warnOnCreditCard,
                warn_on_ssn: warnOnSsn,
                warn_on_password: warnOnPassword,

                // Application
                app_url: appUrl,

                // Unread email notifications
                enable_unread_email_notifications: enableUnreadEmailNotifications,
            } as any);
            setMessage('Settings saved successfully!');
            setTimeout(() => setMessage(null), 3000);
        } catch (error) {
            setMessage('Error saving settings.');
        } finally {
            setSaving(false);
        }
    };

    const handleTypeToggle = (type: string) => {
        if (allowedFileTypes.includes(type)) {
            setAllowedFileTypes(prev => prev.filter(t => t !== type));
        } else {
            setAllowedFileTypes(prev => [...prev, type]);
        }
    };

    const fileTypes = [
        { id: 'image', label: 'Images (jpg, png, gif, webp, svg)' },
        { id: 'video', label: 'Videos (mp4, mov, avi, mkv)' },
        { id: 'document', label: 'Documents (pdf, doc, docx, txt, rtf)' },
        { id: 'data', label: 'Data & Spreadsheets (csv, xls, xlsx, ods)' },
        { id: 'code', label: 'Code & Development (html, css, js, py, sql, etc)' },
        { id: 'audio', label: 'Audio (mp3, wav, ogg, m4a)' },
        { id: 'archive', label: 'Archives (zip, rar, 7z, tar, gz)' }
    ];

    if (!settings) return <div style={{ padding: 20, color: 'var(--text-muted)' }}>Loading settings...</div>;

    return (
        <div style={cardStyle}>
            <div style={headerStyle}>
                <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Global Security Settings</h2>
                <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Configure system-wide policies for all users.</p>
            </div>

            {/* Tab Navigation */}
            <div style={{
                display: 'flex',
                background: 'var(--bg-tertiary)',
                padding: '0 20px',
                borderBottom: '1px solid var(--border-color)',
                gap: 24
            }}>
                {visibleSettingsTabs.map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        style={{
                            padding: '14px 0',
                            background: 'none',
                            border: 'none',
                            borderBottom: activeTab === tab.key ? '2px solid var(--accent-primary)' : '2px solid transparent',
                            color: activeTab === tab.key ? 'var(--text-primary)' : 'var(--text-muted)',
                            fontSize: 13,
                            fontWeight: activeTab === tab.key ? 600 : 400,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                        }}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>


            <div style={{ padding: 20 }}>
                {activeTab === 'general' && (
                    <>
                        <h3 style={{ fontWeight: 600, fontSize: 14, marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid var(--border-color)' }}>
                            Base Configuration
                        </h3>

                        <div style={{ marginBottom: 24 }}>
                            <label style={{ display: 'block', fontWeight: 500, fontSize: 13, marginBottom: 8 }}>
                                Application URL
                            </label>
                            <input
                                className="input"
                                type="url"
                                style={{ width: '100%' }}
                                placeholder="https://chat.example.com"
                                value={appUrl}
                                onChange={e => setAppUrl(e.target.value)}
                            />
                            <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                                This base URL is used to generate absolute links in emails.
                            </p>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={allowProfileEdit}
                                    onChange={(e) => setAllowProfileEdit(e.target.checked)}
                                    style={{ marginTop: 2 }}
                                />
                                <div>
                                    <span style={{ fontWeight: 500, fontSize: 13 }}>Allow Profile Editing</span>
                                    <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                                        Users can update their display name and profile picture.
                                    </p>
                                </div>
                            </label>

                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={requireInviteAcceptance}
                                    onChange={(e) => setRequireInviteAcceptance(e.target.checked)}
                                    style={{ marginTop: 2 }}
                                />
                                <div>
                                    <span style={{ fontWeight: 500, fontSize: 13 }}>Require Invite Acceptance</span>
                                    <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                                        Users must explicitly accept invites before being added to groups.
                                    </p>
                                </div>
                            </label>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={enableReadReceipts}
                                    onChange={(e) => setEnableReadReceipts(e.target.checked)}
                                    style={{ marginTop: 2 }}
                                />
                                <div>
                                    <span style={{ fontWeight: 500, fontSize: 13 }}>Enable Read Receipts</span>
                                    <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                                        Show when messages have been seen by other participants.
                                    </p>
                                </div>
                            </label>

                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={allowMultipleAssignees}
                                    onChange={(e) => setAllowMultipleAssignees(e.target.checked)}
                                    style={{ marginTop: 2 }}
                                />
                                <div>
                                    <span style={{ fontWeight: 500, fontSize: 13 }}>Multiple Assignees</span>
                                    <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                                        Allow tickets to be assigned to more than one agent.
                                    </p>
                                </div>
                            </label>

                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={disableEmojis}
                                    onChange={(e) => setDisableEmojis(e.target.checked)}
                                    style={{ marginTop: 2 }}
                                />
                                <div>
                                    <span style={{ fontWeight: 500, fontSize: 13 }}>Disable Emojis in Message Box</span>
                                    <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                                        Hide the emoji picker from the message composer.
                                    </p>
                                </div>
                            </label>
                        </div>

                        <h3 style={{ fontWeight: 600, fontSize: 14, marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid var(--border-color)', marginTop: 32 }}>
                            Self-Destruct Policy
                        </h3>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
                            <div>
                                <label style={{ display: 'block', fontWeight: 500, fontSize: 13, marginBottom: 8 }}>
                                    Default Expiration Timer (seconds)
                                </label>
                                <input
                                    type="number"
                                    className="input"
                                    value={timer}
                                    onChange={(e) => setTimer(parseInt(e.target.value) || 0)}
                                    style={{ width: '100%' }}
                                />
                                <p style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                                    Messages will auto-delete after this duration if Self-Destruct is enabled.
                                </p>
                            </div>

                            <div style={{ paddingTop: 24 }}>
                                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={allowCustom}
                                        onChange={(e) => setAllowCustom(e.target.checked)}
                                        style={{ marginTop: 2 }}
                                    />
                                    <div>
                                        <span style={{ fontWeight: 500, fontSize: 13 }}>Allow User Customization</span>
                                        <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                                            If unchecked, users cannot change the expiration timer.
                                        </p>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <div style={{ marginBottom: 24 }}>
                            <label style={{ display: 'block', fontWeight: 500, fontSize: 13, marginBottom: 8 }}>
                                Post-Destruction Behavior
                            </label>
                            <div style={{ display: 'flex', gap: 16 }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                                    <input
                                        type="radio"
                                        name="destruction_behavior"
                                        checked={selfDestructBehavior === 'delete'}
                                        onChange={() => setSelfDestructBehavior('delete')}
                                    />
                                    Show "Deleted" Placeholder
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                                    <input
                                        type="radio"
                                        name="destruction_behavior"
                                        checked={selfDestructBehavior === 'vanish'}
                                        onChange={() => setSelfDestructBehavior('vanish')}
                                    />
                                    Vanish Entirely (Hide from list)
                                </label>
                            </div>
                        </div>

                        <div style={{ marginBottom: 24 }}>
                            <label style={{ display: 'block', fontWeight: 500, fontSize: 13, marginBottom: 8 }}>
                                Max Self-Destruct Retention (Hours)
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="8760"
                                value={maxRetentionHours}
                                onChange={(e) => setMaxRetentionHours(parseInt(e.target.value) || 24)}
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    borderRadius: 8,
                                    border: '1px solid var(--border-color)',
                                    background: 'var(--bg-secondary)',
                                    color: 'var(--text-primary)',
                                    fontSize: 14
                                }}
                            />
                        </div>
                    </>
                )}


                {activeTab === 'email' && (
                    <>
                        <div className="form-group" style={{ border: 'none', padding: 0 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600 }}>
                                <Mail size={18} />
                                Email Delivery Service
                            </label>
                            <p style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 20 }}>
                                Configure SMTP or API-based email delivery for invitations, 2FA codes, and password resets.
                            </p>

                            <div style={{ background: 'var(--bg-tertiary)', padding: 20, borderRadius: 8 }}>
                                <div style={{ marginBottom: 20 }}>
                                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 8 }}>Email Provider</label>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        {(['smtp', 'resend', 'gmail'] as const).map(provider => (
                                            <button
                                                key={provider}
                                                type="button"
                                                onClick={() => {
                                                    setEmailProvider(provider);
                                                    if (provider === 'gmail') {
                                                        setSmtpHost('smtp.gmail.com');
                                                        setSmtpPort(587);
                                                        setSmtpSecure(false);
                                                    }
                                                }}
                                                style={{
                                                    padding: '8px 16px',
                                                    borderRadius: 6,
                                                    border: emailProvider === provider ? '2px solid var(--accent-primary)' : '1px solid var(--border-color)',
                                                    background: emailProvider === provider ? 'rgba(54, 197, 240, 0.1)' : 'var(--bg-secondary)',
                                                    color: emailProvider === provider ? 'var(--accent-primary)' : 'var(--text-secondary)',
                                                    cursor: 'pointer',
                                                    fontWeight: emailProvider === provider ? 600 : 400,
                                                    fontSize: 13,
                                                    textTransform: 'uppercase'
                                                }}
                                            >
                                                {provider === 'smtp' ? 'SMTP' : provider === 'resend' ? 'Resend' : 'Gmail'}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {emailProvider === 'resend' && (
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Resend API Key</label>
                                        <input
                                            className="input"
                                            type="password"
                                            style={{ width: '100%' }}
                                            placeholder="re_xxxxx..."
                                            value={resendApiKey}
                                            onChange={e => setResendApiKey(e.target.value)}
                                        />
                                    </div>
                                )}

                                {emailProvider !== 'resend' && (
                                    <>
                                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
                                            <div>
                                                <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>SMTP Host</label>
                                                <input
                                                    className="input"
                                                    style={{ width: '100%' }}
                                                    placeholder={emailProvider === 'gmail' ? 'smtp.gmail.com' : 'smtp.example.com'}
                                                    value={smtpHost}
                                                    onChange={e => setSmtpHost(e.target.value)}
                                                    disabled={emailProvider === 'gmail'}
                                                />
                                            </div>
                                            <div>
                                                <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Port</label>
                                                <input
                                                    className="input"
                                                    type="number"
                                                    style={{ width: '100%' }}
                                                    value={smtpPort}
                                                    onChange={e => setSmtpPort(Number(e.target.value))}
                                                />
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                                            <div>
                                                <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                                                    {emailProvider === 'gmail' ? 'Gmail Address' : 'Username'}
                                                </label>
                                                <input className="input" style={{ width: '100%' }} placeholder="user@gmail.com" value={smtpUser} onChange={e => setSmtpUser(e.target.value)} />
                                            </div>
                                            <div>
                                                <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>
                                                    {emailProvider === 'gmail' ? 'App Password' : 'Password'}
                                                </label>
                                                <input className="input" type="password" style={{ width: '100%' }} value={smtpPassword} onChange={e => setSmtpPassword(e.target.value)} />
                                            </div>
                                        </div>

                                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={smtpSecure}
                                                onChange={(e) => setSmtpSecure(e.target.checked)}
                                            />
                                            <div>
                                                <span style={{ fontWeight: 500, fontSize: 13 }}>Use SSL/TLS (Port 465)</span>
                                            </div>
                                        </label>
                                    </>
                                )}

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    <div>
                                        <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>From Email</label>
                                        <input className="input" style={{ width: '100%' }} placeholder="noreply@yourcompany.com" value={smtpFromEmail} onChange={e => setSmtpFromEmail(e.target.value)} />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>From Name</label>
                                        <input className="input" style={{ width: '100%' }} placeholder="TicketChat" value={smtpFromName} onChange={e => setSmtpFromName(e.target.value)} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Unread Message Email Notifications (Admin Toggle) */}
                        <div style={{ marginTop: 28, padding: 20, background: 'var(--bg-tertiary)', borderRadius: 8, border: '1px solid var(--border-color)' }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                                <div style={{ flex: 1 }}>
                                    <h4 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 4px 0' }}>Unread Message Email Notifications</h4>
                                    <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                                        Allow users to opt in to receive an email digest of unread messages after a
                                        configurable delay. Each user controls their own preference and delay — this
                                        toggle only makes the feature <strong>available</strong> to them.
                                        Requires the Application URL and email to be configured above.
                                    </p>
                                </div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flexShrink: 0 }}>
                                    <input
                                        type="checkbox"
                                        checked={enableUnreadEmailNotifications}
                                        onChange={e => setEnableUnreadEmailNotifications(e.target.checked)}
                                    />
                                    <span style={{ fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap' }}>
                                        {enableUnreadEmailNotifications ? 'Enabled' : 'Disabled'}
                                    </span>
                                </label>
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'storage' && (
                    <>
                        <div className="form-group" style={{ border: 'none', padding: 0 }}>
                            <label style={{ fontSize: 15, fontWeight: 600 }}>File Storage & Limits</label>
                            <p style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 20 }}>
                                Configure where file attachments are stored and global upload restrictions.
                            </p>

                            <div style={{ marginBottom: 24, background: 'var(--bg-tertiary)', padding: 20, borderRadius: 8 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 12 }}>Storage Provider</label>
                                <select
                                    className="input"
                                    style={{ width: '100%', maxWidth: 400 }}
                                    value={storageProvider}
                                    onChange={(e) => setStorageProvider(e.target.value as 'local' | 's3')}
                                >
                                    <option value="local">Local Server Storage (uploads/ directory)</option>
                                    <option value="s3">S3 Compatible Storage (AWS, R2, Backblaze)</option>
                                </select>

                                {storageProvider === 's3' && (
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 20 }}>
                                        <div style={{ gridColumn: 'span 2' }}>
                                            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Endpoint URL</label>
                                            <input className="input" style={{ width: '100%' }} placeholder="https://..." value={s3Endpoint} onChange={e => setS3Endpoint(e.target.value)} />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Region</label>
                                            <input className="input" style={{ width: '100%' }} value={s3Region} onChange={e => setS3Region(e.target.value)} />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Bucket Name</label>
                                            <input className="input" style={{ width: '100%' }} value={s3Bucket} onChange={e => setS3Bucket(e.target.value)} />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Access Key ID</label>
                                            <input className="input" style={{ width: '100%' }} value={s3AccessKey} onChange={e => setS3AccessKey(e.target.value)} />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Secret Access Key</label>
                                            <input className="input" type="password" style={{ width: '100%' }} value={s3SecretKey} onChange={e => setS3SecretKey(e.target.value)} />
                                        </div>
                                        <div style={{ gridColumn: 'span 2' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                                <input type="checkbox" checked={s3IgnoreSsl} onChange={e => setS3IgnoreSsl(e.target.checked)} />
                                                <span style={{ fontSize: 13 }}>Ignore SSL Errors (Unsafe - Testing only)</span>
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                                <div>
                                    <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 8 }}>Max File Size (MB)</label>
                                    <input
                                        type="number"
                                        className="input"
                                        value={maxFileSize}
                                        onChange={(e) => setMaxFileSize(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 8 }}>Max Upload Size (MB)</label>
                                    <input
                                        type="number"
                                        className="input"
                                        value={maxUploadSize}
                                        onChange={(e) => setMaxUploadSize(Number(e.target.value))}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </div>

                            <div style={{ marginTop: 24 }}>
                                <label style={{ fontSize: 13, fontWeight: 500, display: 'block', marginBottom: 12 }}>Allowed File Types</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                                    {fileTypes.map(type => (
                                        <label key={type.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                                            <input
                                                type="checkbox"
                                                checked={allowedFileTypes.includes(type.id)}
                                                onChange={() => handleTypeToggle(type.id)}
                                            />
                                            {type.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'security' && (
                    <>
                        <div className="form-group" style={{ border: 'none', padding: 0 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600 }}>
                                <Shield size={18} />
                                Multi-Factor Authentication (MFA/2FA)
                            </label>

                            <div style={{ margin: '16px 0', background: 'var(--bg-tertiary)', padding: 20, borderRadius: 8 }}>
                                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', marginBottom: 16 }}>
                                    <input
                                        type="checkbox"
                                        checked={require2FA}
                                        onChange={(e) => setRequire2FA(e.target.checked)}
                                        style={{ marginTop: 2 }}
                                    />
                                    <div>
                                        <span style={{ fontWeight: 500, fontSize: 13 }}>Require 2FA for All Users</span>
                                        <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                                            Force all users to verify their identity via email code. Admins and Super-Admins are always required to use 2FA.
                                        </p>
                                    </div>
                                </label>

                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Whitelisted IP Addresses (Bypass 2FA)</label>
                                    <input
                                        className="input"
                                        style={{ width: '100%' }}
                                        placeholder="192.168.1.1, 10.0.0.0/8"
                                        value={whitelistedIPs}
                                        onChange={e => setWhitelistedIPs(e.target.value)}
                                    />
                                    <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                                        Comma-separated list of IPs that skip the 2FA code check.
                                    </p>
                                </div>

                                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', marginTop: 16 }}>
                                    <input
                                        type="checkbox"
                                        checked={requireOauthRecovery}
                                        onChange={(e) => setRequireOauthRecovery(e.target.checked)}
                                        style={{ marginTop: 2 }}
                                    />
                                    <div>
                                        <span style={{ fontWeight: 500, fontSize: 13 }}>Require OAuth Recovery Account</span>
                                        <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                                            When enabled, users must link a Google or Microsoft account before accessing the app.
                                            Users who haven't linked will be blocked at login with a prompt to connect.
                                        </p>
                                    </div>
                                </label>
                            </div>

                            <h3 style={{ fontWeight: 600, fontSize: 14, marginBottom: 16, marginTop: 32, paddingBottom: 8, borderBottom: '1px solid var(--border-color)' }}>
                                Encryption Policy
                            </h3>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 8 }}>Channels</label>
                                    <select className="input" style={{ width: '100%' }} value={channelEncryptionMode} onChange={e => setChannelEncryptionMode(e.target.value as any)}>
                                        <option value="optional">Optional</option>
                                        <option value="forced">Required</option>
                                        <option value="disabled">Disabled</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 8 }}>Direct Messages</label>
                                    <select className="input" style={{ width: '100%' }} value={dmEncryptionMode} onChange={e => setDmEncryptionMode(e.target.value as any)}>
                                        <option value="optional">Optional</option>
                                        <option value="forced">Required</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 8 }}>Topics</label>
                                    <select className="input" style={{ width: '100%' }} value={topicEncryptionMode} onChange={e => setTopicEncryptionMode(e.target.value as any)}>
                                        <option value="optional">Optional</option>
                                        <option value="forced">Required</option>
                                        <option value="disabled">Disabled</option>
                                    </select>
                                </div>
                            </div>

                            <h3 style={{ fontWeight: 600, fontSize: 14, marginBottom: 16, marginTop: 32, paddingBottom: 8, borderBottom: '1px solid var(--border-color)' }}>
                                E2EE Recovery & Escrow
                            </h3>

                            <div style={{ background: 'rgba(234, 179, 8, 0.08)', border: '1px solid rgba(234, 179, 8, 0.3)', borderRadius: 8, padding: '12px 16px', display: 'flex', gap: 10, marginBottom: 16 }}>
                                <AlertTriangle size={16} style={{ color: '#eab308', flexShrink: 0, marginTop: 1 }} />
                                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                                    Configure either Escrow or OAuth recovery (or both) to ensure users can recover history if they lose their password.
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Escrow Recovery Email</label>
                                    <input className="input" type="email" style={{ width: '100%' }} value={escrowEmailRecipient} onChange={e => setEscrowEmailRecipient(e.target.value)} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Google Client ID</label>
                                    <input className="input" value={googleClientId} onChange={e => setGoogleClientId(e.target.value)} />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginTop: 16 }}>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Microsoft Client ID</label>
                                    <input className="input" value={microsoftClientId} onChange={e => setMicrosoftClientId(e.target.value)} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Microsoft Tenant ID</label>
                                    <input className="input" value={microsoftTenantId} onChange={e => setMicrosoftTenantId(e.target.value)} />
                                </div>
                            </div>

                            <h3 style={{ fontWeight: 600, fontSize: 14, marginBottom: 16, marginTop: 32, paddingBottom: 8, borderBottom: '1px solid var(--border-color)' }}>
                                Sensitive Data Detection
                            </h3>

                            <div style={{ background: 'var(--bg-tertiary)', padding: 20, borderRadius: 8 }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={warnOnCreditCard}
                                            onChange={(e) => setWarnOnCreditCard(e.target.checked)}
                                            style={{ marginTop: 2 }}
                                        />
                                        <div>
                                            <span style={{ fontWeight: 500, fontSize: 13 }}>Credit Card Numbers</span>
                                            <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                                                Detect and warn when 14-16 digit card numbers are detected.
                                            </p>
                                        </div>
                                    </label>

                                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={warnOnSsn}
                                            onChange={(e) => setWarnOnSsn(e.target.checked)}
                                            style={{ marginTop: 2 }}
                                        />
                                        <div>
                                            <span style={{ fontWeight: 500, fontSize: 13 }}>Social Security Numbers</span>
                                            <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                                                Detect and warn on US SSN patterns.
                                            </p>
                                        </div>
                                    </label>

                                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={warnOnPassword}
                                            onChange={(e) => setWarnOnPassword(e.target.checked)}
                                            style={{ marginTop: 2 }}
                                        />
                                        <div>
                                            <span style={{ fontWeight: 500, fontSize: 13 }}>Passwords & API Keys</span>
                                            <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                                                Detect potential passwords via keywords and high-entropy strings.
                                            </p>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            <h3 style={{ fontWeight: 600, fontSize: 14, marginBottom: 16, marginTop: 32, paddingBottom: 8, borderBottom: '1px solid var(--border-color)' }}>
                                Group Encryption Overrides
                            </h3>

                            <p style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 16 }}>
                                Set specific encryption modes for individual groups.
                            </p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {channels.map(channel => (
                                    <div key={channel.id} style={{
                                        padding: 12,
                                        background: 'var(--bg-tertiary)',
                                        borderRadius: 8,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            {channel.is_private ? <Lock size={16} /> : <Hash size={16} />}
                                            <span style={{ fontWeight: 500, fontSize: 13 }}>{channel.name}</span>
                                        </div>
                                        <select
                                            className="input"
                                            style={{ width: 180, padding: '4px 8px', fontSize: 12 }}
                                            value={channel.encryption_mode || 'optional'}
                                            onChange={(e) => updateChannel(channel.id, { encryption_mode: e.target.value as any })}
                                        >
                                            <option value="optional">User Optional</option>
                                            <option value="forced">Force Encryption</option>
                                            <option value="disabled">Disable Encryption</option>
                                        </select>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </>
                )}

                {activeTab === 'data' && (
                    <>
                        <div style={{ marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid var(--border-color)' }}>
                            <h3 style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>File Retention Policy</h3>
                            <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>Automate lifecycle management for files and attachments.</p>
                        </div>

                        <div style={{ background: 'var(--bg-tertiary)', padding: 20, borderRadius: 8, marginBottom: 24 }}>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', marginBottom: 20 }}>
                                <input type="checkbox" checked={fileRetentionEnabled} onChange={e => setFileRetentionEnabled(e.target.checked)} />
                                <div>
                                    <span style={{ fontWeight: 500, fontSize: 13 }}>Enable Automatic File Cleanup</span>
                                    <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>Periodically delete old attachments based on size and age.</p>
                                </div>
                            </label>

                            {fileRetentionEnabled && (
                                <>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Large File Threshold (MB)</label>
                                            <input type="number" className="input" style={{ width: '100%' }} value={fileSizeThreshold} onChange={e => setFileSizeThreshold(Number(e.target.value))} />
                                        </div>
                                        <div />
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Small File Retention (Days)</label>
                                            <input type="number" className="input" style={{ width: '100%' }} value={smallFileRetentionDays} onChange={e => setSmallFileRetentionDays(Number(e.target.value))} />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Large File Retention (Days)</label>
                                            <input type="number" className="input" style={{ width: '100%' }} value={largeFileRetentionDays} onChange={e => setLargeFileRetentionDays(Number(e.target.value))} />
                                        </div>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Small Cleanup Interval (Hours)</label>
                                            <input type="number" className="input" style={{ width: '100%' }} value={smallFileCleanupInterval} onChange={e => setSmallFileCleanupInterval(Number(e.target.value))} />
                                        </div>
                                        <div>
                                            <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 }}>Large Cleanup Interval (Hours)</label>
                                            <input type="number" className="input" style={{ width: '100%' }} value={largeFileCleanupInterval} onChange={e => setLargeFileCleanupInterval(Number(e.target.value))} />
                                        </div>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                            <input type="checkbox" checked={smallFileCleanupActive} onChange={e => setSmallFileCleanupActive(e.target.checked)} />
                                            <span style={{ fontSize: 13 }}>Small File Cleanup Active</span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                            <input type="checkbox" checked={largeFileCleanupActive} onChange={e => setLargeFileCleanupActive(e.target.checked)} />
                                            <span style={{ fontSize: 13 }}>Large File Cleanup Active</span>
                                        </label>
                                    </div>

                                    <div style={{ marginBottom: 20 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                            <input type="checkbox" checked={enableGracePeriod} onChange={e => setEnableGracePeriod(e.target.checked)} />
                                            <span style={{ fontSize: 13, fontWeight: 500 }}>Enable Grace Period</span>
                                        </label>
                                        {enableGracePeriod && (
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 12, marginLeft: 24 }}>
                                                <div>
                                                    <label style={{ fontSize: 11, fontWeight: 500 }}>Small File Grace (Hours)</label>
                                                    <input type="number" className="input" style={{ width: '100%', marginTop: 4 }} value={smallFileGracePeriod} onChange={e => setSmallFileGracePeriod(Number(e.target.value))} />
                                                </div>
                                                <div>
                                                    <label style={{ fontSize: 11, fontWeight: 500 }}>Large File Grace (Hours)</label>
                                                    <input type="number" className="input" style={{ width: '100%', marginTop: 4 }} value={largeFileGracePeriod} onChange={e => setLargeFileGracePeriod(Number(e.target.value))} />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                            <input type="checkbox" checked={showRetentionWarning} onChange={e => setShowRetentionWarning(e.target.checked)} />
                                            <span style={{ fontSize: 13 }}>Show Users Warning</span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                            <input type="checkbox" checked={allowUserNeverDelete} onChange={e => setAllowUserNeverDelete(e.target.checked)} />
                                            <span style={{ fontSize: 13 }}>Allow "Never Delete" Requests</span>
                                        </label>
                                    </div>

                                    <div style={{ marginBottom: 20 }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                            <input type="checkbox" checked={enableExpiryNotifications} onChange={e => setEnableExpiryNotifications(e.target.checked)} />
                                            <span style={{ fontSize: 13, fontWeight: 500 }}>Enable Expiry Notifications</span>
                                        </label>
                                        {enableExpiryNotifications && (
                                            <div style={{ marginTop: 8, marginLeft: 24 }}>
                                                <label style={{ fontSize: 11, fontWeight: 500 }}>Notify Before (Days)</label>
                                                <input type="number" className="input" style={{ width: 100, marginLeft: 12 }} value={retentionNotifyBefore} onChange={e => setRetentionNotifyBefore(Number(e.target.value))} />
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 32 }}>
                            <div style={{ padding: 16, backgroundColor: 'var(--bg-tertiary)', borderRadius: 8, border: '1px solid var(--border-color)' }}>
                                <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Small Files Cleanup</h4>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    style={{ width: '100%' }}
                                    onClick={async () => {
                                        setRunningCleanup(true);
                                        try {
                                            const res = await fetch('/api/admin/run-cleanup', {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ category: 'small' })
                                            });
                                            const data = await res.json();
                                            alert(data.message || (data.success ? 'Success' : 'Failed'));
                                        } catch (e) {
                                            alert('Error running cleanup');
                                        } finally {
                                            setRunningCleanup(false);
                                        }
                                    }}
                                    disabled={runningCleanup}
                                >
                                    {runningCleanup ? 'Cleaning...' : 'Manual Cleanup (Small)'}
                                </button>
                            </div>

                            <div style={{ padding: 16, backgroundColor: 'var(--bg-tertiary)', borderRadius: 8, border: '1px solid var(--border-color)' }}>
                                <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Large Files Cleanup</h4>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    style={{ width: '100%' }}
                                    onClick={async () => {
                                        setRunningCleanup(true);
                                        try {
                                            const res = await fetch('/api/admin/run-cleanup', {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ category: 'large' })
                                            });
                                            const data = await res.json();
                                            alert(data.message || (data.success ? 'Success' : 'Failed'));
                                        } catch (e) {
                                            alert('Error running cleanup');
                                        } finally {
                                            setRunningCleanup(false);
                                        }
                                    }}
                                    disabled={runningCleanup}
                                >
                                    {runningCleanup ? 'Cleaning...' : 'Manual Cleanup (Large)'}
                                </button>
                            </div>
                        </div>

                        <h3 style={{ fontWeight: 600, fontSize: 14, marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid var(--border-color)', marginTop: 32 }}>
                            Optimization Settings
                        </h3>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                <input type="checkbox" checked={enableFileDeduplication} onChange={e => setEnableFileDeduplication(e.target.checked)} />
                                <div>
                                    <span style={{ fontWeight: 500, fontSize: 13 }}>File Deduplication</span>
                                    <p style={{ color: 'var(--text-muted)', fontSize: 11 }}>Save space by reusing identical files.</p>
                                </div>
                            </label>
                            <div />
                        </div>

                        <div style={{ background: 'var(--bg-tertiary)', padding: 20, borderRadius: 8 }}>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', marginBottom: 20 }}>
                                <input type="checkbox" checked={enableImageCompression} onChange={e => setEnableImageCompression(e.target.checked)} />
                                <div>
                                    <span style={{ fontWeight: 500, fontSize: 13 }}>Auto Image Compression</span>
                                    <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>Automatically reduce image file size on upload.</p>
                                </div>
                            </label>

                            {enableImageCompression && (
                                <div style={{ marginLeft: 28 }}>
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 8 }}>Compression Quality (1-100)</label>
                                        <input type="number" className="input" style={{ width: 100 }} min="1" max="100" value={imageCompressionQuality} onChange={e => setImageCompressionQuality(Number(e.target.value))} />
                                        <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>85 is the sweet spot for size vs quality.</p>
                                    </div>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={allowUserDisableCompression} onChange={e => setAllowUserDisableCompression(e.target.checked)} />
                                        <span style={{ fontSize: 13 }}>Allow Users to Opt-Out per Upload</span>
                                    </label>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {activeTab === 'desktop' && (
                    <>
                        <div style={{ marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid var(--border-color)' }}>
                            <h3 style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Desktop App Configuration</h3>
                            <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>Manage offline sync and storage for desktop users.</p>
                        </div>

                        <div style={{ background: 'var(--bg-tertiary)', padding: 20, borderRadius: 8 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 8 }}>Sync History (Days)</label>
                                    <input type="number" className="input" style={{ width: '100%' }} value={desktopSyncWindowDays} onChange={e => setDesktopSyncWindowDays(parseInt(e.target.value) || 0)} />
                                </div>
                                <div>
                                    <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 8 }}>Attachment Sync (Days)</label>
                                    <input type="number" className="input" style={{ width: '100%' }} value={desktopAttachmentSyncDays} onChange={e => setDesktopAttachmentSyncDays(parseInt(e.target.value) || 0)} />
                                </div>
                            </div>

                            <div style={{ marginBottom: 24 }}>
                                <label style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 8 }}>Max Messages per Channel</label>
                                <input type="number" className="input" style={{ width: '100%', maxWidth: 200 }} value={desktopMaxMessages} onChange={e => setDesktopMaxMessages(parseInt(e.target.value) || 0)} />
                                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Prevents excessive local storage usage on desktop.</p>
                            </div>

                            <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                                <input type="checkbox" checked={desktopAllowAttachments} onChange={e => setDesktopAllowAttachments(e.target.checked)} />
                                <div>
                                    <span style={{ fontWeight: 500, fontSize: 13 }}>Global Attachment Sync</span>
                                    <p style={{ color: 'var(--text-muted)', fontSize: 11 }}>If disabled, attachments will only load on-demand when clicked.</p>
                                </div>
                            </label>
                        </div>
                    </>
                )}

                <div style={{ borderTop: '1px solid var(--border-color)', marginTop: 32, paddingTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12 }}>
                    {message && (
                        <span style={{ fontSize: 13, color: message.includes('Error') ? '#dc2626' : '#16a34a' }}>
                            {message}
                        </span>
                    )}
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="btn btn-primary"
                        style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                        <Save size={16} />
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div >
    );
}
